import * as React from 'react';
import styled, {css} from 'styled-components';
import {transparentize} from 'polished';
import {getList} from 'api/user';
import Input, {InputProps} from 'components/Input';
import {UserObject} from 'stores/UserStore';
import useClickOutside from 'hooks/use-click-outside';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import {transition, visuallyHidden} from 'helpers/css';
import {IconLoading} from 'icons';

export interface UserPickerProps {
  inputProps?: Omit<InputProps, 'onChange'>;
  onSelect?(user: UserObject): void;
}

const Container = styled.div`
  position: relative;
`;

const Users = styled.div<{
  isFocused?: boolean;
}>`
  position: absolute;
  top: 100%;
  right: 0;
  left: 0;
  
  ${transition()};
  border-radius: 5px;
  background-color: ${({theme}) => `
    ${theme.variables.accentBackgroundColor};
  `};
  max-height: 231px;
  overflow-y: auto;
  display: grid;
  
  ${({isFocused}) => isFocused ? css`
    margin-top: 6px;
    opacity: 1;
  ` : css`
    ${visuallyHidden};
    opacity: 0;
  `};
`;

const User = styled.button`
  ${transition()};
  box-sizing: border-box;
  padding: 11px 14px;
  
  text-align: left;
  font-size: 15px;
  line-height: 20px;
  
  &:hover,
  &:focus-visible {
    background-color: ${({theme}) => `
      ${transparentize(.97, theme.colors.white)};
    `};
  }
  
  &:active {
    background-color: ${({theme}) => `
      ${transparentize(.93, theme.colors.white)};
    `};
  }
`;

/**
 * This component renders the user's selection.
 * @param {UserPickerProps} props
 * @return {React.ReactElement<UserPickerProps>}
 * @constructor
 */
export default function UserPicker(
    props: UserPickerProps,
): React.ReactElement<UserPickerProps> {
  const ref = React.createRef<HTMLDivElement>();

  const [
    search,
    setSearch,
  ] = React.useState<string>('');

  const [
    users,
    setUsers,
  ] = React.useState<UserObject[]>([]);

  const [
    isLoaded,
    setLoaded,
  ] = React.useState<boolean>(false);

  const [
    isFocused,
    setFocused,
  ] = React.useState<boolean>(false);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setLoaded(false);

      getList({
        name: search,
      }).then((response) => {
        validateResponse(response);

        if (response && !isError(response)) {
          setUsers(response);
          setLoaded(true);
        }
      });
    }, 600);
    return () => clearTimeout(timeout);
  }, [search]);

  useClickOutside(ref, () => {
    setFocused(false);
  });

  /**
   * Render input.
   * @return {JSX.Element}
   */
  function renderInput(): JSX.Element {
    /**
     * This focus event handler.
     */
    function handleFocus(): void {
      setFocused(true);
    }

    /**
     * This change event handler.
     * @param {React.ChangeEvent<HTMLInputElement>} event
     */
    function handleChange(
        event: React.ChangeEvent<HTMLInputElement>,
    ): void {
      setSearch(event.target.value);
    }

    return (
      <Input
        {...props.inputProps}
        onChange={handleChange}
        onFocus={handleFocus}
      />
    );
  }

  /**
   * Render users.
   * @return {JSX.Element}
   */
  function renderUsers(): JSX.Element {
    /**
     * Render the user.
     * @param {UserObject} user
     * @param {number} index
     * @return {JSX.Element}
     */
    function renderUser(
        user: UserObject,
        index: number,
    ): JSX.Element {
      /**
       * This click event handler.
       */
      function handleClick(): void {
        setFocused(false);
        props.onSelect && (
          props.onSelect(user)
        );
      }

      return (
        <User key={index} onClick={handleClick}>
          {user.name}
        </User>
      );
    }

    return (
      <Users isFocused={isFocused}>
        {isLoaded ? (
          users.map((user, index) => (
            renderUser(user, index)
          ))
        ) : <IconLoading />}
      </Users>
    );
  }

  return (
    <Container ref={ref}>
      {renderInput()}
      {renderUsers()}
    </Container>
  );
}
